import {ElixhauserFlag, HccFlag} from 'src/contracts/getGrouperDataResponse';

const mergeWith = require('lodash.mergewith');
import {DrgResponseDto} from 'src/contracts/drgResponseDto';
import {DiagnosisCode, FlagChip} from 'src/contracts/diagnosisCode';
import {isEmpty} from 'src/common/arrayUtil';
import {Code} from 'src/contracts/code';

export function getDiagnosesFromCalculationResult(drgResponseDtos: DrgResponseDto[], primaryMs: boolean): DiagnosisCode[] {
    const codes: DiagnosisCode[] = [];
    if (isEmpty(drgResponseDtos)) {
        return codes;
    }

    for (let i = 0; i < drgResponseDtos.length; i++) {
        const drgResponse = drgResponseDtos[i];
        //We don't want to update diagnosis codes based on APR DRG Trucode Responses because these responses don't have cc/mcc info.
        //We will want to update this logic when we add the SOI/ROM logic so that this is more dynamic

        if (drgResponse.diagnoses_results) {
            for (let j = 0; j < drgResponse.diagnoses_results.length; j++) {
                const drgDiagnosisFlags = drgResponse.diagnoses_results[j];
                const drgDiagnosisDescription = drgResponse?.diagnoses?.find(dxDesc => dxDesc.value == drgDiagnosisFlags.code);

                const elixFlags: ElixhauserFlag[] = [];

                drgResponse.elixhauser_code_results?.find(codeResult => codeResult.code == drgDiagnosisFlags.code)?.flags?.forEach((category) => {
                    const foundCategory = drgResponse.elixhauser_flags?.find(({flag}) => flag == category)
                    if (foundCategory) elixFlags.push({code: foundCategory.flag, description: foundCategory.description})
                });

                const diagnosisCode: DiagnosisCode = createDxCodeWithChipInfo(j, drgDiagnosisFlags.code, codes.length == 0,
                  drgDiagnosisDescription?.medium_description || drgDiagnosisDescription?.short_description, drgDiagnosisFlags && drgDiagnosisFlags.poa,
                  drgDiagnosisFlags.mcc_flag, drgDiagnosisFlags.cc_flag, drgDiagnosisFlags.hac_flag,
                  drgDiagnosisDescription?.hccs, elixFlags, drgDiagnosisFlags.soi_level, drgDiagnosisFlags.rom_level, !primaryMs && drgResponse.grouper_type === 'APR'
                )

                if (!codes.some(code => code.code == drgDiagnosisFlags.code)) {
                    codes.push(diagnosisCode);
                }
            }
        }
    }

    return codes;
}

export function createDxCodeWithChipInfo(id: number, code: string, pdx: boolean, description?: string, poa?: string,
                                         mcc?: boolean, cc?: boolean, hac?: boolean,
                                         hccs?: HccFlag[], elix?: ElixhauserFlag[], soi_level?: string, rom_level?: string, addSoiRomChips?: boolean ) {

    const flags: string[] = [];
    const drgModifers: string[] = [];
    const qualityFlags = [];
    const riskFlags: FlagChip[] = [];
    let soi = null;
    let rom = null;

    if (addSoiRomChips){
      if (soi_level && soi_level !== '' && soi_level !== '0') soi = soi_level;
      if (rom_level && rom_level !== '' && rom_level !== '0') rom = rom_level;
    } else {
      if (mcc) {
        flags.push('MCC')
        drgModifers.push('MCC')
      }
      if (cc) {
        flags.push('CC')
        drgModifers.push('CC')
      }
    }

    if (hac) {
        flags.push('HAC')
        qualityFlags.push({name: 'HAC', tooltip: ['(HAC) Hospital Acquired Condition']})
    }
    if (elix && !isEmpty(elix)){
        const elixTooltips = elix.map((elixFlag: ElixhauserFlag ) => elixFlag.description)
        riskFlags.push({name: 'ELIX', tooltip: elixTooltips});
    }

    if (hccs && !isEmpty(hccs)){
        const hccTooltips = hccs.map((hccFlag: HccFlag ) => `${hccFlag.value} - ${hccFlag.description}`)
        riskFlags.push({name: 'HCC', tooltip: hccTooltips})
    }

    return <DiagnosisCode>{
        id: id,
        action: undefined,
        code: code,
        description: description,
        pdx: pdx,
        drgModifier: drgModifers,
        present_on_admission: poa,
        flags: flags,
        hcc: hccs,
        elixhauser_flags: elix,
        quality: qualityFlags,
        risk: riskFlags,
        soi_level: soi,
        rom_level: rom
    }
}

export function getProceduresFromCalculationResult(drgResponseDtos: DrgResponseDto[]): Code[] {
    const codes: Code[] = [];
    if (isEmpty(drgResponseDtos)) {
        return codes;
    }

    for (let i = 0; i < drgResponseDtos.length; i++) {
        const drgResponse = drgResponseDtos[i];

        if (drgResponse.procedures_results) {
            for (let j = 0; j < drgResponse.procedures_results.length; j++) {
                const drgProcedureFlags = drgResponse.procedures_results[j];
                const drgProcedureDescription = drgResponse?.procedures?.find(procDesc => procDesc.value == drgProcedureFlags.code);

                codes.push({
                    code: drgProcedureFlags.code,
                    description: drgProcedureDescription?.medium_description || drgProcedureDescription?.short_description,
                    drgModifier: drgProcedureFlags ? (drgProcedureFlags.or_flag ? ['OR'] : []) : [],
                    id: j
                });
            }
        }
    }

    return codes;
}

export function mergeDiagnosisLists(
  base: DiagnosisCode[],
  changed: DiagnosisCode[],
  resequence: boolean,
  codesFromPrimaryCalc: boolean  // whether the dx codes we are merging are the result of a primary drg calc
): DiagnosisCode[] {
    if (isEmpty(base) || isEmpty(changed) || !codesFromPrimaryCalc) {
        return base;
    }

    let mergedList: DiagnosisCode[] = [];

    if (!resequence) {
        mergedList = base;
    } else {
        // init the merged list with the reordered codes from resequencing
        // then add any additional codes the user may have added since the last
        // calculate request
        changed.forEach(code => {
            const baseCode = base.find(x => x.code.toLowerCase() == code.code.toLowerCase());
            if (baseCode) {
                mergedList.push(baseCode);
            }
        });

        base.forEach(code => {
            // any codes from the base that aren't in our merged list get added to the end
            if (!mergedList.find(x => x.code.toLowerCase() == code.code.toLowerCase())) {
                mergedList.push(code);
            }
        });
    }

    // first apply updates
    for (let i = 0; i < mergedList.length; i++) {
        const code = mergedList[i];
        if (!code.code) {
            continue;
        }
        const changedCode = changed.find(x => x.code && x.code.toLowerCase() === code.code.toLowerCase());
        if (changedCode) {
            mergedList[i] = mergeCode(code, changedCode, codesFromPrimaryCalc);
        }
    }

    return mergedList;
}

const secondaryPropExclusions = [
  'soi_level',
  'rom_level',
  'flags',
  'drgModifier'
];

export function mergeCode<T>(base: T, changed: T, codeFromPrimaryCalc = true): T {

    return mergeWith(base, changed, (objValue: unknown, srcValue: unknown, key: string) => {

        if (!codeFromPrimaryCalc && secondaryPropExclusions.some(x => x === key)) { // don't update excluded fields from secondary calcs
            return objValue;
        } else if (Array.isArray(srcValue)) { // replace arrays instead of merging
            return srcValue;
        } else if (key === 'present_on_admission') { // don't update POA
            return objValue;
        }
    });
}

export function mergeProcedureLists(base: Code[], changed: Code[], codesFromPrimaryCalc: boolean): Code[] {

    if (isEmpty(base) || isEmpty(changed) || !codesFromPrimaryCalc) {
        return base;
    }

    const mergedList: Code[] = base;

    // first apply updates
    for (let i = 0; i < mergedList.length; i++) {
        const code = mergedList[i];
        if (!code.code) {
            continue;
        }
        const changedCode = changed.find(x => x.code && x.code.toLowerCase() === code.code.toLowerCase());
        if (changedCode) {
            mergedList[i] = mergeCode(code, changedCode);
        }
    }

    return mergedList;
}
